import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Tabs, Tab, Stack, TextField, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, IconButton, Typography,
  CircularProgress, Chip, Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { toast } from 'react-toastify';
import PageHeader from '../../components/PageHeader';
import ConfirmDialog from '../../components/ConfirmDialog';
import { getCategories, createCategory, updateCategory, deleteCategory } from '../../api/endpoints/categories';

const CategoryList = () => {
  const [tab, setTab] = useState(0);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', description: '' });
  const [editing, setEditing] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const catType = tab === 0 ? 'product' : 'blog';

  const fetchCategories = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getCategories({ type: catType });
      setCategories(res.data.data || []);
    } catch {
      toast.error('Failed to load categories');
    } finally {
      setLoading(false);
    }
  }, [catType]);

  useEffect(() => { fetchCategories(); }, [fetchCategories]);

  const resetForm = () => {
    setForm({ name: '', description: '' });
    setEditing(null);
  };

  const handleEdit = (cat) => {
    setForm({ name: cat.name, description: cat.description || '' });
    setEditing(cat._id);
  };

  const handleSave = async () => {
    if (!form.name) { toast.error('Name is required'); return; }
    try {
      if (editing) {
        await updateCategory(editing, { name: form.name, description: form.description, type: catType });
        toast.success('Category updated');
      } else {
        await createCategory({ name: form.name, description: form.description, type: catType });
        toast.success('Category created');
      }
      resetForm();
      fetchCategories();
    } catch {
      toast.error('Failed to save category');
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleteLoading(true);
    try {
      await deleteCategory(deleteId);
      toast.success('Category deleted');
      setDeleteId(null);
      fetchCategories();
    } catch {
      toast.error('Failed to delete category');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleTabChange = (e, newTab) => {
    setTab(newTab);
    resetForm();
  };

  return (
    <Box>
      <PageHeader title="Categories" subtitle="Manage product and blog categories" />
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 3 }}>
        <Tab label="Product Categories" />
        <Tab label="Blog Categories" />
      </Tabs>
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
          {editing ? 'Edit Category' : 'Add New Category'}
        </Typography>
        <Stack direction="row" spacing={2} alignItems="flex-end">
          <TextField
            label="Name"
            value={form.name}
            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
            size="small"
            sx={{ flex: 1 }}
          />
          <TextField
            label="Description"
            value={form.description}
            onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
            size="small"
            sx={{ flex: 1 }}
          />
          <Button variant="contained" startIcon={editing ? <EditIcon /> : <AddIcon />} onClick={handleSave}>
            {editing ? 'Update' : 'Add'}
          </Button>
          {editing && <Button onClick={resetForm}>Cancel</Button>}
        </Stack>
      </Paper>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      ) : categories.length === 0 ? (
        <Typography color="text.secondary" textAlign="center" py={4}>No categories found</Typography>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Name</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Slug</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((cat) => (
                <TableRow key={cat._id}>
                  <TableCell>{cat.name}</TableCell>
                  <TableCell>{cat.description || '-'}</TableCell>
                  <TableCell><Chip label={cat.slug} size="small" variant="outlined" /></TableCell>
                  <TableCell>
                    <Chip
                      label={cat.isActive ? 'Active' : 'Inactive'}
                      color={cat.isActive ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Edit">
                        <IconButton size="small" onClick={() => handleEdit(cat)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton size="small" color="error" onClick={() => setDeleteId(cat._id)}>
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
      <ConfirmDialog
        open={!!deleteId}
        title="Delete Category"
        content="Are you sure you want to delete this category?"
        loading={deleteLoading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
    </Box>
  );
};

export default CategoryList;